import { useContext, useMemo, useState } from 'react';
import { ANALYTICS_EVENT } from '@/common/constants/analyticsEvents';
import { LanguageContext } from '@/common/contexts/LanguageContext';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { useAnalytics } from '@/common/contexts/AnalyticsContext';
import { getStripeCountryByLang } from '@/pages/customer/pawall/stripe/stripeCountryByLang';
import { usePlanPaymentConfirm } from '../../hooks/usePlanPaymentConfirm';
import { getPlanPaymentElementOptions } from '../../helpers/getPlanPaymentElementOptions';
import PaymentElementField from './PaymentElementField';
import PlanCheckoutLegalText from './PlanCheckoutLegalText';
import {
	BillingNote,
	ButtonSpinner,
	ErrorMessage,
	FormContainer,
	PayButton,
	SecureNote,
} from './PlanPayment.styles';

const PlanPaymentForm = ({
	intent,
	selectedPlan,
	returnPath,
	analyticsSource,
	submitLabel,
	processingLabel,
	termsPrefix,
	billingNote,
	paymentMethodOrder,
	initialErrorMessage,
	onConfirmError,
}) => {
	const t = useLanguageData('chat');
	const { lang } = useContext(LanguageContext);
	const { user } = useGuestAuth();
	const { trackAnalytics } = useAnalytics();
	const billingCountry = getStripeCountryByLang(lang);
	const [isPaymentReady, setIsPaymentReady] = useState(false);

	const { stripe, elements, isProcessing, errorMessage, handleSubmit } =
		usePlanPaymentConfirm({
			intent,
			returnPath,
			email: user?.email,
			billingCountry,
			initialErrorMessage,
			onConfirmError,
		});

	const paymentElementOptions = useMemo(
		() =>
			getPlanPaymentElementOptions({
				billingCountry,
				email: user?.email,
				paymentMethodOrder,
			}),
		[billingCountry, user?.email, paymentMethodOrder],
	);

	const handleFormSubmit = (event) => {
		if (analyticsSource) {
			trackAnalytics(ANALYTICS_EVENT.PLAN.CHECKOUT_CLICK, {
				planName: selectedPlan.name,
				source: analyticsSource,
			});
		}
		return handleSubmit(event);
	};

	return (
		<FormContainer onSubmit={handleFormSubmit} $processing={isProcessing}>
			<PaymentElementField
				options={paymentElementOptions}
				isReady={isPaymentReady}
				onReady={() => setIsPaymentReady(true)}
			/>

			{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

			<PayButton
				type='submit'
				disabled={!stripe || !elements || isProcessing || !isPaymentReady}
				aria-busy={isProcessing}
			>
				{isProcessing && <ButtonSpinner aria-hidden='true' />}
				<span>{isProcessing ? processingLabel : submitLabel}</span>
			</PayButton>

			{billingNote && <BillingNote>{billingNote}</BillingNote>}

			<SecureNote>{t.planCheckoutSecureStripe}</SecureNote>

			<PlanCheckoutLegalText prefix={termsPrefix} />
		</FormContainer>
	);
};

export default PlanPaymentForm;
